import React, { createContext, useContext, useEffect } from "react";
import type { Clarification } from "../../../types/clarification.type";
import { useClarificationsData, type ClarificationsDataReturn } from "../hooks/useClarificationsData";
import { useFilterState, type FilterStateReturn } from "../hooks/useFilterState";
import { useSelectionState, type SelectionStateReturn } from "../hooks/useSelectionState";
import { useModalState, type ModalStateReturn } from "../hooks/useModalState";

interface SupplierOption {
  id: number;
  name: string;
  logo?: string | null;
}

export interface ClarificationsRegisterProps {
  allSuppliers?: SupplierOption[];
  injectedClarifications?: Clarification[];
  isClientView: boolean;
  openSupplierOnClick?: boolean;
  rfxId?: number;
  rfxSubmissionId?: number;
  supplierFilter?: number;
  supplierName: string;
  onDraftCountChange?: (draftCount: number) => void;
  onRowClickOverride?: (clarification: Clarification) => void | null;
}

export interface ClarificationsRegisterContextValue {
  data: ClarificationsDataReturn;
  filters: FilterStateReturn;
  modals: ModalStateReturn;
  props: {
    allSuppliers?: SupplierOption[];
    isClientView: boolean;
    rfxId?: number;
    rfxSubmissionId?: number;
    supplierName: string;
  };
  selection: SelectionStateReturn;
}

const ClarificationsRegisterContext = createContext<ClarificationsRegisterContextValue | null>(null);

export const useClarificationsRegisterContext = (): ClarificationsRegisterContextValue => {
  const context = useContext(ClarificationsRegisterContext);
  if (!context) {
    throw new Error("useClarificationsRegisterContext must be used within a ClarificationsRegisterProvider");
  }
  return context;
};

export const ClarificationsRegisterProvider: React.FC<ClarificationsRegisterProps & { children: React.ReactNode }> = ({
  allSuppliers,
  injectedClarifications,
  openSupplierOnClick = false,
  rfxId,
  rfxSubmissionId,
  supplierName,
  isClientView,
  supplierFilter,
  onDraftCountChange,
  onRowClickOverride,
  children,
}) => {
  const data = useClarificationsData({
    rfxId,
    rfxSubmissionId,
    isClientView,
    injectedClarifications,
    supplierFilter,
  });

  const filters = useFilterState({
    suppliers: data.suppliers,
    displayClarifications: data.displayClarifications,
    supplierFilter,
  });

  const selection = useSelectionState({
    selectableItems: data.selectableItems,
  });

  const modals = useModalState({
    rfxId,
    isClientView,
    clarifications: data.clarifications,
    addClarification: data.addClarification,
    editClarification: data.editClarification,
    removeClarification: data.removeClarification,
    sendDraftClarifications: data.sendDraftClarifications,
    sendSelectedResponses: data.sendSelectedResponses,
    saveDraftResponse: data.saveDraftResponse,
    handleValidateImport: data.handleValidateImport,
    handleImport: data.handleImport,
    updateLocalClarifications: data.updateLocalClarifications,
    refetch: data.refetch,
    clearSelection: selection.clearSelection,
    selectedIds: selection.selectedIds,
    openSupplierOnClick,
    onRowClickOverride,
  });

  useEffect(() => {
    if (onDraftCountChange) {
      const draftCount = data.selectableItems.filter((c) => c.id > 0).length;
      onDraftCountChange(draftCount);
    }
  }, [data.selectableItems, onDraftCountChange]);

  const value: ClarificationsRegisterContextValue = {
    data,
    filters,
    selection,
    modals,
    props: {
      isClientView,
      rfxId,
      rfxSubmissionId,
      supplierName,
      allSuppliers,
    },
  };

  return (
    <ClarificationsRegisterContext.Provider value={value}>
      {children}
    </ClarificationsRegisterContext.Provider>
  );
};
